import React from 'react';
import { Link } from 'react-router-dom';
import LegalPage from '../../components/LegalPage';
import legalConfig from '../../lib/legalConfig';
import { useLanguage } from '../../context/LanguageContext';

const META_DESC =
  'CosmoEstates Terms & Conditions: the rules for using our Cyprus property marketplace, listings, lead-generation features, paid plans, and your rights as a consumer.';

export default function TermsPage() {
  const { t } = useLanguage();
  return (
    <LegalPage
      title={t('legal.docs.termsTitle')}
      effectiveDate={legalConfig.effectiveDate}
      lastUpdated={legalConfig.lastUpdated}
      metaTitle="Terms & Conditions | CosmoEstates"
      metaDescription={META_DESC}
    >
      <div className="legal-meta">
        <p>
          <strong>{legalConfig.brand}</strong> — operated by{' '}
          <strong>{legalConfig.entityName}</strong>, a company registered in the Republic of
          Cyprus under registration number {legalConfig.companyRegistrationNumber}, registered
          office at {legalConfig.registeredAddress} ("<strong>CosmoEstates</strong>",
          "<strong>we</strong>", "<strong>us</strong>", "<strong>our</strong>").
        </p>
        <p>
          <strong>Website:</strong>{' '}
          <a href={legalConfig.websiteUrl}>{legalConfig.websiteUrl}</a> ("
          <strong>the Platform</strong>")
        </p>
        <p>
          <strong>Contact:</strong> {legalConfig.contactEmail}
        </p>
      </div>

      <p className="legal-draft-note">
        Draft for review by Cyprus-qualified counsel. Not legal advice.
      </p>

      <h2>1. About these Terms</h2>
      <p>
        1.1 These Terms &amp; Conditions ("<strong>Terms</strong>") govern your access to and use
        of the Platform and all features, tools, and services we make available through it
        (together, the "<strong>Services</strong>").
      </p>
      <p>
        1.2 By creating an account, listing a property, using any tool, or otherwise using the
        Platform, you confirm that you accept these Terms. If you do not accept them, do not use
        the Platform.
      </p>
      <p>
        1.3 These Terms incorporate by reference our <Link to="/privacy">Privacy Policy</Link>,{' '}
        <Link to="/cookies">Cookie Policy</Link>, <Link to="/disclaimer">Disclaimer</Link>, and{' '}
        <Link to="/acceptable-use">Acceptable Use &amp; Listing Policy</Link>. Where paid
        features are used, the{' '}
        <Link to="/billing">Subscription, Boost &amp; Refund Terms</Link> also apply.
      </p>
      <p>
        1.4 We may update these Terms from time to time. We will post the updated version with a
        new "Last updated" date and, where changes are material, take reasonable steps to notify
        you. Continued use after changes take effect means you accept the updated Terms.
      </p>

      <h2>2. What CosmoEstates is — and is not</h2>
      <p>
        2.1 CosmoEstates is an <strong>online marketplace and information-society service</strong>{' '}
        that allows users to publish, search, and discover property listings, and to use related
        digital tools. We act as a neutral <strong>intermediary / hosting platform</strong> in
        the sense of the Cyprus Electronic Commerce Law 156(I)/2004 (implementing Directive
        2000/31/EC).
      </p>
      <p>
        2.2 <strong>We are not a real estate agent.</strong> Unless we expressly state otherwise
        in writing, we do not act as an estate agent, broker, valuer, mortgage adviser, financial
        adviser, surveyor, contractor, or legal adviser. We do not negotiate transactions,
        represent buyers or sellers, or take commission on property sales or rentals. We are not
        a party to any agreement, viewing, transaction, or communication between users.
      </p>
      <p>
        2.3 We do not own, control, inspect, endorse, or guarantee any property, listing, price,
        or user, and we do not verify the accuracy of listings or of any information users
        provide. Any checks we perform (for example for duplicate, low-quality, or suspicious
        listings) are done at our discretion and do not amount to a warranty.
      </p>
      <p>
        2.4 Any decision you make in relation to a property — buying, selling, renting,
        renovating, borrowing, or appointing an agent or contractor — is your own decision. You
        should carry out your own due diligence and obtain independent professional advice before
        acting.
      </p>

      <h2>3. Eligibility and accounts</h2>
      <p>
        3.1 You must be at least 18 years old and able to enter into a binding contract to use
        the Services.
      </p>
      <p>
        3.2 Some features require an account. You agree to provide accurate, current information
        and to keep it up to date. You are responsible for keeping your login credentials secure
        and for all activity under your account. Notify us promptly at {legalConfig.contactEmail}{' '}
        of any unauthorised use.
      </p>
      <p>
        3.3 You may have a different status on the Platform — for example{' '}
        <strong>private user</strong>, <strong>independent agent</strong>, or{' '}
        <strong>partner agent</strong> — and additional terms may apply to each (see section 7).
        We may verify the status, identity, or licensing of professional users.
      </p>
      <p>
        3.4 We may suspend or close an account that breaches these Terms, is used unlawfully, or
        poses a risk to other users or to us.
      </p>

      <h2>4. Listings and user content</h2>
      <p>
        4.1 "<strong>User Content</strong>" means anything you submit to the Platform, including
        listings, images, descriptions, prices, messages, reviews, and form submissions.
      </p>
      <p>
        4.2 You are solely responsible for your User Content and for ensuring you have the right
        to post it. You represent and warrant that your User Content is accurate, not misleading,
        lawful, and does not infringe any third party's rights (including intellectual property
        and data protection rights), and that you are entitled to advertise the property
        concerned.
      </p>
      <p>
        4.3 You must comply with the{' '}
        <Link to="/acceptable-use">Acceptable Use &amp; Listing Policy</Link>, which forms part
        of these Terms. We may, without obligation and at our discretion, refuse, edit, suspend,
        or remove any User Content — for example where it appears to be duplicated, low quality,
        a scam, fraudulent, or otherwise in breach of these Terms — and we may report unlawful
        content to the authorities.
      </p>
      <p>
        4.4 <strong>Licence to us.</strong> You grant us a non-exclusive, worldwide, royalty-free
        licence to host, store, reproduce, adapt (for formatting and display), publish, and
        display your User Content for the purpose of operating, promoting, and improving the
        Services. This licence ends when the content is removed, except to the extent we must
        retain copies by law or in backups.
      </p>
      <p>
        4.5 <strong>Listing limits.</strong> Posting allowances (for example the number of
        listings you may publish in a period) are as described on the Platform and may change.
        We currently allow generous posting but may introduce limits in future on notice.
      </p>

      <h2>5. Tools and estimates</h2>
      <p>
        5.1 The Platform offers digital tools, which may include a{' '}
        <strong>Property Price Estimate</strong>, a <strong>Renovation cost tool</strong>,{' '}
        <strong>Neighbourhood Intelligence</strong>, and a{' '}
        <strong>Financial Compass / mortgage calculator</strong>.
      </p>
      <p>
        5.2 These tools produce{' '}
        <strong>
          automated, indicative, AI-generated outputs for general information only
        </strong>
        . They are <strong>not</strong> valuations, surveys, appraisals, financial advice,
        mortgage advice, investment advice, or guarantees of cost, value, or outcome. Figures
        may be inaccurate or out of date. You must independently verify any figure and consult
        appropriately qualified professionals (valuers, contractors, mortgage providers,
        financial advisers) before relying on it. Full terms are in the{' '}
        <Link to="/disclaimer">Disclaimer</Link>.
      </p>

      <h2>6. Lead generation and introductions (CPL)</h2>
      <p>
        6.1 Certain features — for example <strong>Find Agent</strong>,{' '}
        <strong>Find Technician</strong>, and <strong>Property Price Estimate</strong> follow-ups
        — invite you to submit your contact details and requirements so that you can be connected
        with agents, contractors, or other third-party professionals ("
        <strong>Providers</strong>").
      </p>
      <p>
        6.2 If you submit such a form,{' '}
        <strong>
          you understand and agree that we may share the personal data and details you provide
          with one or more Providers
        </strong>
        , including on a paid "cost-per-lead" (CPL) basis, so that they can contact you. How we
        handle this data is explained in the <Link to="/privacy">Privacy Policy</Link>. Do not
        submit a form if you do not want to be contacted by Providers.
      </p>
      <p>
        6.3 We do not select, employ, supervise, endorse, or guarantee any Provider, and we are
        not responsible for the services, conduct, pricing, quality, or advice of any Provider.
        Any contract you enter with a Provider is solely between you and that Provider.
      </p>

      <h2>7. Professional users: agents, partners, and contractors</h2>
      <p>
        7.1 Professional users (independent agents, partner agents, contractors, renovators) may
        access additional features such as receiving leads and listing more properties, subject
        to the fees and arrangements agreed between us (for example a flat fee, a per-agent fee,
        and/or CPL charges).
      </p>
      <p>
        7.2 Professional users represent and warrant that they hold all registrations, licences,
        and insurance required by Cyprus law to provide their services — including, where
        applicable, registration with the Real Estate Agents Registration Council under the Real
        Estate Agents Law of 2010 — and that they will comply with all applicable laws,
        including consumer protection and data protection law, when dealing with leads and
        clients obtained through the Platform.
      </p>
      <p>
        7.3 Leads provided to professional users are provided <strong>"as is"</strong>. We do
        not warrant that any lead will result in business, that contact details are accurate, or
        that a user will respond. Specific commercial terms (fees, lead volumes, payment,
        refunds for invalid leads, term and termination) are set out in the separate
        partner/agent agreement or order form between us.
      </p>

      <h2>8. Fees and paid features</h2>
      <p>
        8.1 Some features are paid, including <strong>CosmoEstates Pro</strong> subscriptions,{' '}
        <strong>Boost Listing</strong> packages, professional/partner fees, CPL charges, and (in
        future) advertising. Prices, billing cycles, cancellation, and refund rights are set out
        in the <Link to="/billing">Subscription, Boost &amp; Refund Terms</Link>.
      </p>
      <p>
        8.2 Unless stated otherwise, prices are in euro (€) and include or exclude VAT as
        indicated at checkout. You are responsible for any taxes that apply to you.
      </p>

      <h2>9. Intellectual property</h2>
      <p>
        9.1 The Platform, its design, software, databases, text, graphics, logos (including the
        CosmoEstates name and logo), and tool outputs (excluding User Content) are owned by or
        licensed to us and are protected by intellectual property laws. We grant you a limited,
        non-exclusive, non-transferable, revocable licence to use the Platform for its intended
        purpose.
      </p>
      <p>
        9.2 You may not copy, scrape, harvest, reproduce, resell, or create derivative works
        from the Platform or its data (including bulk extraction of listings) without our prior
        written consent.
      </p>

      <h2>10. Prohibited conduct</h2>
      <p>
        You agree not to: (a) use the Platform unlawfully or for fraud; (b) post false,
        misleading, or infringing content; (c) scrape, crawl, or bulk-download data except as
        expressly permitted; (d) interfere with, overload, or attempt to gain unauthorised access
        to the Platform or its systems; (e) introduce malware; (f) misuse other users' personal
        data, including data received as a Provider, other than as permitted by law and these
        Terms; or (g) circumvent fees, limits, or security measures. Further detail is in the{' '}
        <Link to="/acceptable-use">Acceptable Use &amp; Listing Policy</Link>.
      </p>

      <h2>11. Third-party links and services</h2>
      <p>
        The Platform may contain links to, or integrations with, third-party websites and
        services (for example mapping, payment, analytics, or data providers). We are not
        responsible for third-party content or services and your use of them is at your own risk
        and subject to their terms.
      </p>

      <h2>12. Disclaimers and warranties</h2>
      <p>
        12.1 The Platform and Services are provided <strong>"as is"</strong> and{' '}
        <strong>"as available"</strong>. To the maximum extent permitted by law, we exclude all
        implied warranties, including as to accuracy, availability, fitness for a particular
        purpose, and non-infringement.
      </p>
      <p>
        12.2 We do not warrant that the Platform will be uninterrupted, error-free, secure, or
        that listings, tool outputs, or third-party information are accurate or complete.
      </p>
      <p>
        12.3 Nothing in these Terms limits any rights you have as a consumer under Cyprus law
        that cannot lawfully be excluded (see section 13).
      </p>

      <h2>13. Limitation of liability</h2>
      <p>
        13.1 Nothing in these Terms excludes or limits our liability where it would be unlawful
        to do so, including for death or personal injury caused by our negligence, for fraud, or
        for any liability that cannot be excluded under the Cyprus Consumer Protection Law
        112(I)/2021 or other mandatory law.
      </p>
      <p>13.2 Subject to clause 13.1, and to the maximum extent permitted by law:</p>
      <ul>
        <li>
          (a) we are not liable for any loss arising from your dealings with other users or
          Providers, from any listing or User Content, from reliance on any tool output or
          estimate, or from any property transaction;
        </li>
        <li>
          (b) we are not liable for indirect, special, or consequential loss, or for loss of
          profit, revenue, business, goodwill, anticipated savings, or data; and
        </li>
        <li>
          (c) our total aggregate liability to you arising out of or in connection with the
          Services in any 12-month period is limited to the greater of (i) the total fees you
          paid us in that period, or (ii) €{legalConfig.liabilityCap}.
        </li>
      </ul>

      <h2>14. Indemnity</h2>
      <p>
        You agree to indemnify and hold us harmless from claims, losses, and costs (including
        reasonable legal fees) arising from your User Content, your use of the Services, your
        breach of these Terms, or your breach of any law or third-party right — including any
        misuse by you (as a Provider) of personal data obtained through the Platform.
      </p>

      <h2>15. Suspension and termination</h2>
      <p>15.1 You may stop using the Platform and close your account at any time.</p>
      <p>
        15.2 We may suspend or terminate your access, with or without notice, if you breach
        these Terms or any policy, if required by law, or to protect the Platform or its users.
        Sections that by their nature should survive termination (including 4.4, 9, 12, 13, 14,
        and 17) will survive.
      </p>

      <h2>16. Notices</h2>
      <p>
        We may give notice via the Platform, by email to your account address, or by posting on
        the Platform. You can contact us at {legalConfig.contactEmail}.
      </p>

      <h2>17. Governing law and disputes</h2>
      <p>
        17.1 These Terms and any dispute arising out of or in connection with them are governed
        by the laws of the Republic of Cyprus.
      </p>
      <p>
        17.2 The courts of the Republic of Cyprus have jurisdiction, except that, if you are a
        consumer, you may also benefit from the mandatory protections and any competent courts
        available to you under the law of your place of residence in the EU.
      </p>
      <p>
        17.3 Consumers may also be entitled to use out-of-court dispute resolution, including
        the EU Online Dispute Resolution platform and the competent Cyprus consumer dispute
        bodies.
      </p>

      <h2>18. General</h2>
      <p>18.1 If any provision is found invalid, the rest remains in force.</p>
      <p>18.2 Our failure to enforce a right is not a waiver of it.</p>
      <p>
        18.3 You may not transfer your rights under these Terms without our consent; we may
        transfer ours as part of a reorganisation or sale of the business.
      </p>
      <p>
        18.4 These Terms (with the documents they incorporate) are the entire agreement between
        you and us regarding the Services.
      </p>

      <hr />

      <p>
        <em>Questions about these Terms: {legalConfig.contactEmail}.</em>
      </p>
    </LegalPage>
  );
}
